/**
 * BACKUP IMPORT
 *
 * Restores a collection from a Polish ALL the Things export (.zip or its
 * data.json). Items are already in native shape, so they're written directly
 * (deduped by itemType|brand|name) — no column mapping. Photos are re-uploaded
 * from each item's embedded `photos[].url` via the existing URL photo importer
 * (works as long as the original photos still exist in Storage).
 *
 * NOTE: zip-FILE photo restore (for accounts whose originals were already
 * purged, so the URLs are dead) is a planned follow-up.
 */

import { useState } from 'react';
import { collection, addDoc, getDocs, Timestamp } from 'firebase/firestore';
import JSZip from 'jszip';
import { Loader2, Upload, CheckCircle, AlertCircle } from 'lucide-react';
import { db } from '../firebase/config';
import { getCurrentUser } from '../firebase/auth';
import { importPhotoFromUrl } from '../utils/urlPhotoImport';

interface Props {
  onClose: () => void;
}

type Stage = 'select' | 'review' | 'importing' | 'done';

interface ParsedBackup {
  version?: number;
  items: any[];
  exportedAt?: string;
}

export default function BackupImport({ onClose }: Props) {
  const [stage, setStage] = useState<Stage>('select');
  const [error, setError] = useState('');
  const [parsed, setParsed] = useState<ParsedBackup | null>(null);
  const [withPhotos, setWithPhotos] = useState(true);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [result, setResult] = useState<{
    imported: number;
    skipped: number;
    photos: number;
    photoErrors: number;
  } | null>(null);

  const photoCount = (items: any[]) =>
    items.reduce((n, it) => n + (Array.isArray(it.photos) ? it.photos.filter((p: any) => p?.url).length : 0), 0);

  const handleFile = async (file: File) => {
    setError('');
    try {
      let text: string;
      if (file.name.toLowerCase().endsWith('.zip')) {
        const zip = await JSZip.loadAsync(file);
        const entry = zip.file('data.json') ?? zip.file(/(^|\/)data\.json$/i)[0];
        if (!entry) throw new Error('No data.json found inside that zip.');
        text = await entry.async('string');
      } else {
        text = await file.text();
      }
      const data = JSON.parse(text);
      if (!data || !Array.isArray(data.items)) {
        throw new Error("That doesn't look like a Polish ALL the Things export (no items found).");
      }
      setParsed({ version: data.version, items: data.items, exportedAt: data.exportedAt });
      setStage('review');
    } catch (e: any) {
      setError(e?.message ?? 'Could not read that file.');
    }
  };

  const runImport = async () => {
    const user = getCurrentUser();
    if (!user || !parsed) return;
    setStage('importing');
    setProgress(0);
    setStatus('Checking your existing collection…');

    const items = parsed.items.filter((it) => it && it.brand && it.name && !it.isDeleted);

    // Dedup against existing items (itemType|brand|name).
    const existing = await getDocs(collection(db, 'users', user.uid, 'items'));
    const existingKeys = new Set(
      existing.docs.map((d) => {
        const x = d.data();
        return `${x.itemType}|${(x.brand || '').toLowerCase()}|${(x.name || '').toLowerCase()}`;
      })
    );

    let imported = 0;
    let skipped = 0;
    let photos = 0;
    let photoErrors = 0;
    const photoJobs: { id: string; url: string }[] = [];

    // Pass 1: write item records.
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      setStatus(`Restoring ${it.brand} – ${it.name}…`);
      const key = `${it.itemType}|${(it.brand || '').toLowerCase()}|${(it.name || '').toLowerCase()}`;
      if (existingKeys.has(key)) {
        skipped++;
        setProgress(Math.round(((i + 1) / items.length) * 60));
        continue;
      }

      const oldPhotos: any[] = Array.isArray(it.photos) ? it.photos : [];
      const itemData: any = { ...it };
      delete itemData.id;
      delete itemData.userId;
      delete itemData.photos;
      delete itemData.slug; // re-stamped by the catalog trigger
      delete itemData.dateAdded;
      delete itemData.updatedAt;
      itemData.userId = user.uid;
      itemData.dateAdded = Timestamp.now();
      itemData.importedFrom = 'backup';
      itemData.importedAt = Timestamp.now();
      // Always derive lowercase mirrors from the (possibly already present)
      // brand/name. Older backups may not have these fields; newer ones do.
      // Either way, the post-import state is consistent for search + dupes.
      if (typeof itemData.brand === 'string') {
        itemData.brandLower = itemData.brand.toLowerCase();
      }
      if (typeof itemData.name === 'string') {
        itemData.nameLower = itemData.name.toLowerCase();
      }

      const ref = await addDoc(collection(db, 'users', user.uid, 'items'), itemData);
      imported++;

      if (withPhotos) {
        for (const p of oldPhotos) {
          if (p?.url) photoJobs.push({ id: ref.id, url: p.url });
        }
      }
      setProgress(Math.round(((i + 1) / items.length) * 60));
    }

    // Pass 2: re-upload photos from their original URLs.
    for (let i = 0; i < photoJobs.length; i++) {
      const job = photoJobs[i];
      setStatus(`Restoring photos… (${i + 1} of ${photoJobs.length})`);
      const r = await importPhotoFromUrl(user.uid, job.id, job.url);
      if (r.status === 'success') photos++;
      else if (r.status === 'failed') photoErrors++;
      setProgress(60 + Math.round(((i + 1) / Math.max(1, photoJobs.length)) * 40));
    }

    setProgress(100);
    setResult({ imported, skipped, photos, photoErrors });
    setStage('done');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="glass-modal max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-glass font-body">Restore from a backup</h3>
          {stage !== 'importing' && (
            <button onClick={onClose} className="text-glass-secondary hover:text-glass">✕</button>
          )}
        </div>

        {stage === 'select' && (
          <div>
            <p className="text-glass-secondary text-sm mb-4">
              Upload a Polish ALL the Things export — the <strong>.zip</strong> you were emailed, or
              its <strong>data.json</strong>. Your items will be restored (duplicates skipped), and
              photos re-uploaded from the export.
            </p>
            <label className="glass-card p-6 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-slate-800/60 transition-default border-2 border-dashed border-slate-600">
              <Upload className="icon-lg text-purple-400" />
              <span className="text-glass-secondary text-sm">Choose your export (.zip or .json)</span>
              <input
                type="file"
                accept=".zip,.json,application/zip,application/json"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              />
            </label>
            <p className="text-glass-muted text-xs mt-3">
              Note: photo restore works while the original photos still exist (e.g. restoring or
              moving accounts). If the original account was already purged, only the item data
              restores — recovering photos from the zip itself is coming later.
            </p>
            {error && (
              <div className="mt-3 flex items-start gap-2 text-red-400 text-sm">
                <AlertCircle className="icon-sm flex-shrink-0 mt-0.5" /> {error}
              </div>
            )}
          </div>
        )}

        {stage === 'review' && parsed && (
          <div>
            <p className="text-glass-secondary text-sm mb-4">
              Found <strong>{parsed.items.length} items</strong>
              {photoCount(parsed.items) > 0 && <> and <strong>{photoCount(parsed.items)} photos</strong></>}
              {parsed.exportedAt && <> (exported {new Date(parsed.exportedAt).toLocaleDateString()})</>}.
              Items already in your collection are skipped.
            </p>
            <label className="flex items-center gap-2 mb-5 cursor-pointer">
              <input type="checkbox" checked={withPhotos} onChange={(e) => setWithPhotos(e.target.checked)} />
              <span className="text-glass-secondary text-sm">Also restore photos</span>
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => { setStage('select'); setParsed(null); }}
                className="flex-1 px-4 py-2 bg-slate-800/40 text-glass-secondary rounded-lg hover:bg-slate-700 transition-default font-medium"
              >
                Back
              </button>
              <button
                onClick={runImport}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-default font-medium"
              >
                Restore
              </button>
            </div>
          </div>
        )}

        {stage === 'importing' && (
          <div className="py-4">
            <div className="flex items-center gap-2 text-glass mb-3">
              <Loader2 className="icon-sm animate-spin" /> {status}
            </div>
            <div className="h-2 rounded-full bg-slate-700 overflow-hidden">
              <div className="h-full bg-purple-600 transition-all" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-glass-muted text-xs mt-2">Please keep this tab open until it finishes.</p>
          </div>
        )}

        {stage === 'done' && result && (
          <div className="py-2">
            <div className="flex items-center gap-2 text-green-400 mb-3">
              <CheckCircle className="icon-md" /> <span className="font-bold">Restore complete</span>
            </div>
            <ul className="text-glass-secondary text-sm space-y-1 mb-5">
              <li>{result.imported} items restored</li>
              {result.skipped > 0 && <li>{result.skipped} skipped (already in your collection)</li>}
              {withPhotos && <li>{result.photos} photos restored{result.photoErrors > 0 && `, ${result.photoErrors} couldn't be fetched`}</li>}
            </ul>
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-default font-medium"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
